import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 각 월별 섹션의 스크롤 위치를 추적하고 가장 적합한 월을 판단하는 훅
 * @param {Array} monthsList - 월별 데이터 배열 (사용하지 않지만 인터페이스 유지)
 * @param {Function} onVisibilityChange - 활성 월이 변경될 때 호출되는 콜백
 * @param {Object} options - 설정 옵션
 * @param {number} options.topOffset - 상단 offset (픽셀, 기본값: 100)
 * @param {number} options.bottomOffset - 하단 offset (픽셀, 기본값: 200)
 * @param {number} options.transitionPoint - 전환 지점 (0~1, 기본값: 0.3)
 */
export const useMonthVisibility = (
  monthsList, 
  onVisibilityChange, 
  options = {}
) => {
  const {
    topOffset = 100,        // 상단 100px 지나면 "사라짐"
    bottomOffset = 200,     // 하단 200px 들어오면 "나타남"
    transitionPoint = 0.3,  // 30% 지점에서 전환
  } = options;

  const elementRefs = useRef(new Map());
  const [currentMonth, setCurrentMonth] = useState(null);
  const rafId = useRef(null);

  // 각 월의 스크롤 위치 정보를 계산
  const getMonthPositions = useCallback(() => {
    const positions = [];
    const viewportHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    const viewportTop = scrollTop + topOffset;
    const viewportBottom = scrollTop + viewportHeight - bottomOffset;

    elementRefs.current.forEach((element, monthKey) => {
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const elementTop = scrollTop + rect.top;
      const elementBottom = scrollTop + rect.bottom;

      // 뷰포트 내에 있는지 확인
      const isVisible = elementBottom > viewportTop && elementTop < viewportBottom;
      
      if (isVisible) {
        // 얼마나 보이는지 계산
        const visibleTop = Math.max(elementTop, viewportTop);
        const visibleBottom = Math.min(elementBottom, viewportBottom);
        const visibleHeight = visibleBottom - visibleTop;
        const totalHeight = elementBottom - elementTop;
        const visibilityRatio = visibleHeight / totalHeight;

        positions.push({
          monthKey,
          elementTop,
          elementBottom,
          visibilityRatio,
          // 새로 나타나는 정도 (하단에서 올라올 때)
          entryProgress: Math.max(0, Math.min(1, (viewportBottom - elementTop) / (elementBottom - elementTop)))
        });
      }
    });

    // 위에서 아래 순서로 정렬
    return positions.sort((a, b) => a.elementTop - b.elementTop);
  }, [topOffset, bottomOffset]);

  // 활성 월을 결정하는 로직
  const determineActiveMonth = useCallback(() => {
    const positions = getMonthPositions();
    
    console.log(`🔍 현재 보이는 월들:`, 
      positions.map(p => `${p.monthKey}: ${(p.visibilityRatio * 100).toFixed(1)}% (entry: ${(p.entryProgress * 100).toFixed(1)}%)`)
    );

    if (positions.length === 0) {
      return null;
    }

    // ✅ 1개만 보이면 무조건 그것 선택
    if (positions.length === 1) {
      const onlyMonth = positions[0].monthKey;
      if (onlyMonth !== currentMonth) {
        console.log(`✅ 단 하나의 월(${onlyMonth})만 보임, 즉시 전환`);
      }
      return onlyMonth;
    }

    // 2개 이상 보이는 경우
    const mostVisible = positions.reduce((prev, curr) => 
      curr.visibilityRatio > prev.visibilityRatio ? curr : prev
    );

    // 현재 월이 없으면 가장 많이 보이는 월 선택
    if (!currentMonth) {
      return mostVisible.monthKey;
    }

    // 현재 월이 여전히 보이는지 확인
    const currentPosition = positions.find(p => p.monthKey === currentMonth);
    
    if (!currentPosition) {
      // 현재 월이 완전히 사라짐
      console.log(`🚨 현재 월(${currentMonth})이 뷰포트에서 사라짐, ${mostVisible.monthKey}로 전환`);
      return mostVisible.monthKey;
    }

    // 새로운 월이 전환 지점을 넘었는지 확인
    for (const position of positions) {
      if (position.monthKey !== currentMonth && 
          position.entryProgress >= transitionPoint &&
          position.visibilityRatio > currentPosition.visibilityRatio) {
        console.log(`🔄 ${position.monthKey}가 전환 지점(${(transitionPoint * 100)}%) 넘음, 전환`);
        return position.monthKey;
      }
    }

    // 현재 월 유지
    return currentMonth;
  }, [currentMonth, transitionPoint, getMonthPositions]);

  // 스크롤 핸들러
  const handleScroll = useCallback(() => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    rafId.current = requestAnimationFrame(() => {
      const newActiveMonth = determineActiveMonth();
      
      if (newActiveMonth !== currentMonth) {
        console.log(`📍 활성 월 변경: ${currentMonth || 'none'} → ${newActiveMonth}`);
        setCurrentMonth(newActiveMonth);
        
        if (newActiveMonth) {
          const [year, month] = newActiveMonth.split('-');
          onVisibilityChange({ year, month });
        }
      }
    });
  }, [currentMonth, determineActiveMonth, onVisibilityChange]);

  // 스크롤 이벤트 등록
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // 초기 실행
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [handleScroll]);

  // ref를 등록하는 함수
  const registerRef = useCallback((element, monthKey) => {
    if (!element || !monthKey) return;

    elementRefs.current.set(monthKey, element);
    
    // 등록 후 즉시 체크
    setTimeout(() => handleScroll(), 100);
  }, [handleScroll]);

  // 언마운트 시 정리
  useEffect(() => {
    return () => {
      elementRefs.current.clear();
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  return { registerRef, currentMonth };
};